import { useCallback, useEffect, useMemo, useState } from 'react';
import { type FieldValues, useForm } from 'react-hook-form';
import { flatten, unflatten } from 'flat';
import { type QueryParamConfigMap } from 'serialize-query-params';
import { useDebounceValue } from 'usehooks-ts';

import { isEmpty } from '@/utils/formChangeCounter';

import { useQueryPersistence } from './useQueryPersistence';
import { useQuerySuspender } from './useQuerySuspender';
import { useSuspendableValue } from './useSuspendableValue';

export const enum SortDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export interface Sorting {
  sortBy?: string;
  sortDirection?: SortDirection;
}

export interface Pagination {
  page?: number;
  size?: number;
}

type ValuesWithSortingAndPagination = FieldValues & Sorting & Pagination;

const PAGINATION_KEYS = ['size', 'page'];

const SORTING_KEYS = ['sortBy', 'sortDirection'];

export const SORTING_AND_PAGINATION_KEYS = [...PAGINATION_KEYS, ...SORTING_KEYS];

export const DEFAULT_SORTING: Sorting = {
  sortBy: undefined,
  sortDirection: SortDirection.ASC
};

export const DEFAULT_PAGINATION: Pagination = {
  page: 0,
  size: 10
};

export const getEmptyDate = () => ({ start: null, end: null });

export const getCompoundValuesForSection = (section: string, compoundFormKeys: string[]) =>
  compoundFormKeys.filter(key => key.startsWith(`${section}.`)).map(key => key.replace(`${section}.`, ''));

export const useEntityList = <FormValues extends ValuesWithSortingAndPagination, RequestValues extends FormValues>(
  defaultValues: FormValues,
  urlConfigMap: QueryParamConfigMap,
  fetchData: (request: RequestValues) => void | Promise<void>,
  formToRequestConverter: ((values: FormValues) => RequestValues) | undefined,
  debounceDelay: number
) => {
  const isSuspended = useQuerySuspender(false);
  const [filterFormValue, setFilterFormValue] = useState<FormValues | null>(null);

  const sorting = useMemo(
    () => ({
      sortBy: filterFormValue?.sortBy,
      sortDirection: filterFormValue?.sortDirection
    }),
    [filterFormValue]
  );

  const pagination = useMemo(
    () => ({
      page: filterFormValue?.page,
      size: filterFormValue?.size
    }),
    [filterFormValue]
  );

  const form = useForm({
    defaultValues: defaultValues as FieldValues
  });

  const { watch } = form;

  const currentRequestParams = useMemo(() => {
    if (!filterFormValue) return null;

    const normalizedFilter: FormValues = unflatten(
      Object.fromEntries(
        Object.entries(flatten<object, object>(filterFormValue)).filter(
          ([, value]) => !isEmpty(value as object | object[] | string | number)
        )
      )
    );

    if (!formToRequestConverter) {
      return normalizedFilter as RequestValues;
    }

    return formToRequestConverter(normalizedFilter);
  }, [filterFormValue, formToRequestConverter]);

  const [debouncedRequestParams] = useDebounceValue<RequestValues>(currentRequestParams!, debounceDelay);

  const preparedRequestParams = useSuspendableValue(debouncedRequestParams, isSuspended);

  useEffect(() => {
    if (preparedRequestParams) {
      fetchData(preparedRequestParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(preparedRequestParams)]);

  const handleChangeSort = useCallback(
    (params: { sortBy?: string; sortDirection?: SortDirection }) => {
      form.setValue('sortBy', params.sortBy!);
      form.setValue('sortDirection', params.sortDirection!);
    },
    [form]
  );

  const handleChangePagination = useCallback(
    (params: { size?: number; page?: number }) => {
      form.setValue('size', params.size!);
      form.setValue('page', params.page!);
    },
    [form]
  );

  const defaultQueryValues = useQueryPersistence(defaultValues, filterFormValue, urlConfigMap);

  useEffect(() => {
    const subscription = watch((value: FieldValues) => {
      setFilterFormValue(value as FormValues);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    Object.keys(defaultQueryValues).forEach(key => {
      form.setValue(key, defaultQueryValues[key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultQueryValues]);

  return {
    filterFormValue,
    pagination,
    sorting,
    handleChangePagination,
    handleChangeSort,
    form
  };
};
